/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useState } from "react";
import { Megaphone, Pencil, Plus, Trash2 } from "lucide-react";
import { useAuth } from "../../../context/AuthContext";
import { api } from "../../../services/apiService";
import { Badge } from "../../../components/ops/Badge";
import { CollapsibleRow } from "../../../components/ops/CollapsibleRow";
import { useConfirm } from "../../../components/ops/ConfirmProvider";
import { DataToolbar } from "../../../components/ops/DataToolbar";
import { DateTimeField } from "../../../components/ops/DateTimeField";
import { EmptyState } from "../../../components/ops/EmptyState";
import { Modal } from "../../../components/ops/Modal";
import { PageHeader } from "../../../components/ops/PageHeader";
import { Pagination } from "../../../components/ops/Pagination";
import { useToast } from "../../../components/ops/ToastProvider";
import { usePagedList } from "../../../components/ops/usePagedList";

const audienceLabels = { all: "Everyone", parents: "Parents", coaches: "Coaches & staff" };
const empty = { title: "", body: "", audience: "all", expiresAt: null };

export function AdminAnnouncementsPage() {
  const { accessToken } = useAuth();
  const toast = useToast();
  const confirm = useConfirm();
  const [announcements, setAnnouncements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(empty);
  const [editingId, setEditingId] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [saving, setSaving] = useState(false);
  const [search, setSearch] = useState("");
  const [audienceFilter, setAudienceFilter] = useState("all");

  const filteredAnnouncements = useMemo(() => {
    return announcements.filter((a) => (audienceFilter === "all" ? true : a.audience === audienceFilter));
  }, [announcements, audienceFilter]);

  const { page, setPage, totalPages, totalItems, pageItems } = usePagedList(filteredAnnouncements, {
    search,
    searchFields: ["title", "body"],
    pageSize: 8,
  });

  const load = () => {
    setLoading(true);
    api.get("/announcements/manage")
      .then((response) => setAnnouncements(response.data.announcements || []))
      .catch(() => toast.error("Unable to load announcements"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, [accessToken]);

  const openCreate = () => {
    setEditingId(null);
    setForm(empty);
    setModalOpen(true);
  };

  const openEdit = (announcement) => {
    setEditingId(announcement._id);
    setForm({
      title: announcement.title,
      body: announcement.body,
      audience: announcement.audience,
      expiresAt: announcement.expiresAt ? new Date(announcement.expiresAt) : null,
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setForm(empty);
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = { ...form, expiresAt: form.expiresAt ? form.expiresAt.toISOString() : null };

    try {
      if (editingId) {
        const response = await api.patch(`/announcements/${editingId}`, payload);
        setAnnouncements(announcements.map((a) => (a._id === editingId ? response.data.announcement : a)));
        toast.success("Announcement updated successfully!");
      } else {
        const response = await api.post("/announcements", payload);
        setAnnouncements([response.data.announcement, ...announcements]);
        toast.success("Announcement created successfully!");
      }
      closeModal();
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to save announcement");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (announcement) => {
    const ok = await confirm({
      title: "Delete this announcement?",
      message: `"${announcement.title}" will be permanently removed. This can't be undone.`,
      confirmLabel: "Delete announcement",
      tone: "danger",
    });
    if (!ok) return;

    try {
      await api.delete(`/announcements/${announcement._id}`);
      setAnnouncements(announcements.filter((a) => a._id !== announcement._id));
      toast.success("Announcement deleted.");
    } catch (err) {
      toast.error(err.response?.data?.error?.message || err.message || "Unable to delete announcement");
    }
  };

  const isExpired = (announcement) => announcement.expiresAt && new Date(announcement.expiresAt) < new Date();

  return (
    <div>
      <PageHeader
        title="Announcements"
        subtitle="Post updates that show up on parent and coach dashboards"
        actions={
          <button type="button" className="ops-button ops-button--primary" onClick={openCreate}>
            <Plus size={16} /> New Announcement
          </button>
        }
      />

      <div className="ops-card">
        <DataToolbar
          search={search}
          onSearchChange={setSearch}
          searchPlaceholder="Search by title or message..."
          filters={[
            {
              label: "Audience",
              value: audienceFilter,
              onChange: setAudienceFilter,
              options: [{ value: "all", label: "All audiences" }, ...Object.entries(audienceLabels).map(([value, label]) => ({ value, label }))],
            },
          ]}
          resultCount={totalItems}
          itemLabel="announcements"
        />
        {loading ? (
          <p className="ops-loading">Loading announcements...</p>
        ) : totalItems === 0 ? (
          <EmptyState icon={Megaphone} title="No announcements found" description={announcements.length === 0 ? "Post your first announcement to reach parents and coaches on their dashboards." : "Try adjusting your search or filters."} />
        ) : (
          <>
            <ul className="ops-list">
              {pageItems.map((announcement) => (
                <CollapsibleRow
                  key={announcement._id}
                  avatar={<Megaphone size={16} />}
                  title={
                    <>
                      {announcement.title} <Badge tone="neutral">{audienceLabels[announcement.audience]}</Badge>
                      {isExpired(announcement) && <Badge tone="danger">Expired</Badge>}
                    </>
                  }
                  meta={announcement.expiresAt ? `Expires ${new Date(announcement.expiresAt).toLocaleDateString()}` : "No expiration date"}
                  trailing={
                    <>
                      <button className="ops-button ops-button--outline ops-button--small" onClick={() => openEdit(announcement)}>
                        <Pencil size={13} /> Edit
                      </button>
                      <button className="ops-button ops-button--danger ops-button--small" onClick={() => remove(announcement)} aria-label="Delete announcement">
                        <Trash2 size={13} />
                      </button>
                    </>
                  }
                >
                  <p>{announcement.body}</p>
                </CollapsibleRow>
              ))}
            </ul>
            <Pagination page={page} totalPages={totalPages} onPageChange={setPage} />
          </>
        )}
      </div>

      <Modal
        open={modalOpen}
        onClose={closeModal}
        title={editingId ? "Edit Announcement" : "New Announcement"}
        size="md"
        footer={
          <>
            <button type="submit" form="announcement-form" className="ops-button ops-button--primary" disabled={saving}>
              {saving ? "Saving..." : editingId ? "Save Changes" : "Post Announcement"}
            </button>
            <button type="button" className="ops-button ops-button--secondary" onClick={closeModal}>Cancel</button>
          </>
        }
      >
        <form id="announcement-form" onSubmit={submit} className="ops-form">
          <div className="ops-form-section">
            <p className="ops-form-section-title">Announcement</p>
            <div className="ops-form-group">
              <label className="ops-form-label">Title</label>
              <input required className="ops-input" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
            </div>
            <div className="ops-form-group">
              <label className="ops-form-label">Message</label>
              <textarea required className="ops-textarea" value={form.body} onChange={(e) => setForm({ ...form, body: e.target.value })} />
            </div>
            <div className="ops-form-row">
              <div>
                <label className="ops-form-label">Audience</label>
                <select className="ops-select" value={form.audience} onChange={(e) => setForm({ ...form, audience: e.target.value })}>
                  <option value="all">Everyone</option>
                  <option value="parents">Parents only</option>
                  <option value="coaches">Coaches & staff only</option>
                </select>
              </div>
              <div>
                <label className="ops-form-label">Expires on <span className="ops-form-label-optional">(optional)</span></label>
                <DateTimeField
                  showTime={false}
                  value={form.expiresAt}
                  onChange={(date) => setForm({ ...form, expiresAt: date })}
                  placeholder="No expiration"
                />
              </div>
            </div>
          </div>

        </form>
      </Modal>
    </div>
  );
}
